import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import type { Servico, TipoPreco, ItemOS } from '@/types/servico';
import { useOrganization } from '@/hooks/useOrganization';

// ===========================================
// QUERIES
// ===========================================

// Listar serviços ativos (para atendentes)
export function useServicosAtivos() {
  const { organizationId } = useOrganization();

  return useQuery({
    queryKey: ['servicos', organizationId, 'ativos'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('servicos')
        .select('*')
        .eq('ativo', true)
        .order('nome', { ascending: true });

      if (error) throw error;
      return data as Servico[];
    },
  });
}

// Listar todos os serviços (para admin)
export function useServicos() {
  const { organizationId } = useOrganization();

  return useQuery({
    queryKey: ['servicos', organizationId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('servicos')
        .select('*')
        .order('ativo', { ascending: false })
        .order('nome', { ascending: true });

      if (error) throw error;
      return data as Servico[];
    },
  });
}

// Buscar itens de uma OS específica
export function useItensOS(ordemServicoId: string | undefined) {
  const { organizationId } = useOrganization();

  return useQuery({
    queryKey: ['itens-os', organizationId, ordemServicoId],
    queryFn: async () => {
      if (!ordemServicoId) return [];

      const { data, error } = await supabase
        .from('ordem_servico_itens')
        .select('*')
        .eq('ordem_servico_id', ordemServicoId)
        .order('criado_em', { ascending: true });

      if (error) throw error;
      return data as ItemOS[];
    },
    enabled: !!ordemServicoId,
  });
}

// ===========================================
// MUTATIONS - ADMIN
// ===========================================

// Criar novo serviço
export function useCriarServico() {
  const queryClient = useQueryClient();
  const { requireOrgId } = useOrganization();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async (dados: {
      nome: string;
      descricao?: string;
      tipo_preco: TipoPreco;
      valor_base: number;
      unidade?: string;
      duracao_padrao_minutos?: number | null;
    }) => {
      const { data, error } = await supabase
        .from('servicos')
        .insert([{
          nome: dados.nome,
          descricao: dados.descricao || '',
          tipo_preco: dados.tipo_preco,
          valor_base: dados.valor_base,
          unidade: dados.unidade || 'serviço',
          duracao_padrao_minutos: dados.duracao_padrao_minutos ?? null,
          ativo: true,
          organization_id: requireOrgId(),
        }])
        .select()
        .single();

      if (error) throw error;
      return data as Servico;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['servicos'] });
      toast.success(t('services.toast.created'));
    },
    onError: (error: Error) => {
      console.error('Erro ao criar serviço:', error);
      toast.error(t('services.toast.createError'));
    },
  });
}

// Atualizar serviço existente
export function useAtualizarServico() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async ({
      id,
      dados,
    }: {
      id: string;
      dados: Partial<{
        nome: string;
        descricao: string;
        tipo_preco: TipoPreco;
        valor_base: number;
        unidade: string;
        duracao_padrao_minutos: number | null;
      }>;
    }) => {
      const { data, error } = await supabase
        .from('servicos')
        .update(dados)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as Servico;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['servicos'] });
      toast.success(t('services.toast.updated'));
    },
    onError: (error: Error) => {
      console.error('Erro ao atualizar serviço:', error);
      toast.error(t('services.toast.updateError'));
    },
  });
}

// Ativar/desativar serviço
export function useToggleServicoAtivo() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async ({ id, ativo }: { id: string; ativo: boolean }) => {
      const { data, error } = await supabase
        .from('servicos')
        .update({ ativo })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as Servico;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['servicos'] });
      toast.success(
        data.ativo ? t('services.toast.activated') : t('services.toast.deactivated')
      );
    },
    onError: (error: Error) => {
      console.error('Erro ao alterar status:', error);
      toast.error(t('services.toast.toggleError'));
    },
  });
}

// ===========================================
// MUTATIONS - ITENS OS
// ===========================================

// Criar itens da OS (usado ao criar/editar OS)
export function useCriarItensOS() {
  const queryClient = useQueryClient();
  const { requireOrgId } = useOrganization();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async ({
      ordemServicoId,
      itens,
    }: {
      ordemServicoId: string;
      itens: {
        servico_id: string;
        servico_nome: string;
        quantidade: number;
        valor_unitario: number;
        valor_total: number;
      }[];
    }) => {
      // Primeiro, deletar itens existentes (para caso de edição)
      await supabase
        .from('ordem_servico_itens')
        .delete()
        .eq('ordem_servico_id', ordemServicoId);

      // Inserir novos itens
      if (itens.length > 0) {
        const orgId = requireOrgId();
        const { error } = await supabase
          .from('ordem_servico_itens')
          .insert(
            itens.map((item) => ({
              ordem_servico_id: ordemServicoId,
              servico_id: item.servico_id,
              servico_nome: item.servico_nome,
              quantidade: item.quantidade,
              valor_unitario: item.valor_unitario,
              valor_total: item.valor_total,
              organization_id: orgId,
            }))
          );

        if (error) throw error;
      }

      return true;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['itens-os'] });
    },
    onError: (error: Error) => {
      console.error('Erro ao salvar itens:', error);
      toast.error(t('services.toast.itemsError'));
    },
  });
}

// ===========================================
// MUTATIONS - EXCLUSÃO DE SERVIÇO
// ===========================================

// Excluir serviço (verifica se está em uso)
export function useExcluirServico() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async (id: string) => {
      // 1. Verificar se serviço está em uso em alguma OS
      const { count, error: countError } = await supabase
        .from('ordem_servico_itens')
        .select('*', { count: 'exact', head: true })
        .eq('servico_id', id);

      if (countError) throw countError;

      if (count && count > 0) {
        // Está em uso - apenas desativar (exclusão lógica)
        const { error } = await supabase
          .from('servicos')
          .update({ ativo: false })
          .eq('id', id);

        if (error) throw error;

        return { tipo: 'desativado' as const, count };
      } else {
        // Não está em uso - pode deletar definitivamente
        const { error } = await supabase
          .from('servicos')
          .delete()
          .eq('id', id);

        if (error) throw error;

        return { tipo: 'excluido' as const };
      }
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['servicos'] });

      if (result.tipo === 'desativado') {
        toast.success(
          t('services.toast.deactivatedInUse', { count: result.count })
        );
      } else {
        toast.success(t('services.toast.deleted'));
      }
    },
    onError: (error: Error) => {
      console.error('Erro ao excluir serviço:', error);
      toast.error(t('services.toast.deleteError'));
    },
  });
}
